import React from 'react';
import {
  osc, wobble, lerp, loop, clamp, easeOut, pathLerp, stagger,
} from '../../core/AnimationUtils';

/*
 * Concept-acting SCENES — AI / Machine Learning (Anivaze v2)
 * The concept HAPPENS in front of you; no diagrams with labels.
 * Pure function of `t` (deterministic, exportable).
 * See design-plan/v2/ANIMATION-PATTERN.md for the contract.
 */

/* ━━━ Tiny pictograms rendered as pure SVG (no external images) ━━━━━━━━━━━━ */

/** A cute cartoon cat face, centred at (0,0), radius ~22. */
const CatFace = () => (
  <g>
    <circle r={22} fill="#fde68a" stroke="#92400e" strokeWidth={2} />
    {/* ears */}
    <polygon points="-14,-18 -22,-34 -6,-20" fill="#fde68a" stroke="#92400e" strokeWidth={1.5} />
    <polygon points="14,-18 22,-34 6,-20" fill="#fde68a" stroke="#92400e" strokeWidth={1.5} />
    <polygon points="-13,-20 -19,-31 -7,-21" fill="#fca5a5" />
    <polygon points="13,-20 19,-31 7,-21" fill="#fca5a5" />
    {/* eyes */}
    <ellipse cx={-8} cy={-4} rx={4} ry={5} fill="#1f2937" />
    <ellipse cx={8} cy={-4} rx={4} ry={5} fill="#1f2937" />
    <circle cx={-6.5} cy={-5.5} r={1.5} fill="#fff" />
    <circle cx={9.5} cy={-5.5} r={1.5} fill="#fff" />
    {/* nose + whiskers */}
    <ellipse cx={0} cy={4} rx={3} ry={2} fill="#f9a8d4" />
    <line x1={-18} y1={3} x2={-5} y2={5} stroke="#92400e" strokeWidth={1.2} />
    <line x1={18} y1={3} x2={5} y2={5} stroke="#92400e" strokeWidth={1.2} />
    <line x1={-18} y1={7} x2={-5} y2={6} stroke="#92400e" strokeWidth={1.2} />
    <line x1={18} y1={7} x2={5} y2={6} stroke="#92400e" strokeWidth={1.2} />
    <path d="M-5,7 Q0,12 5,7" fill="none" stroke="#92400e" strokeWidth={1.5} />
  </g>
);

/** A cute cartoon dog face, centred at (0,0), radius ~22. */
const DogFace = () => (
  <g>
    <circle r={22} fill="#d1b48c" stroke="#78350f" strokeWidth={2} />
    {/* floppy ears */}
    <ellipse cx={-20} cy={2} rx={10} ry={16} fill="#b45309" />
    <ellipse cx={20} cy={2} rx={10} ry={16} fill="#b45309" />
    {/* eyes */}
    <ellipse cx={-7} cy={-5} rx={4.5} ry={5} fill="#1f2937" />
    <ellipse cx={7} cy={-5} rx={4.5} ry={5} fill="#1f2937" />
    <circle cx={-5.5} cy={-6.5} r={1.5} fill="#fff" />
    <circle cx={8.5} cy={-6.5} r={1.5} fill="#fff" />
    {/* snout */}
    <ellipse cx={0} cy={6} rx={10} ry={7} fill="#e7c99e" stroke="#78350f" strokeWidth={1} />
    <ellipse cx={0} cy={4} rx={4} ry={2.5} fill="#1f2937" />
    {/* tongue */}
    <path d="M-4,10 Q0,17 4,10" fill="#f9a8d4" stroke="#ec4899" strokeWidth={1} />
  </g>
);

/* ━━━ Machine Learning: robot sorts picture-cards into Cat/Dog bins ━━━━━━━━━ */

export interface MachineLearningSceneProps {
  opacity?: number;
  accentColor?: string;
  label?: string;
  t?: number;
}

export const MachineLearningScene: React.FC<MachineLearningSceneProps> = ({
  opacity = 1,
  accentColor = '#6366f1',
  label = 'Computers learn by seeing lots of examples!',
  t = 0,
}) => {
  /* ── timing constants ── */
  const CARD_PERIOD = 2.4;          // seconds per card cycle
  const cardPhase = loop(t, CARD_PERIOD);   // 0→1 within each card arrival

  // Which card index we're on — increases every CARD_PERIOD seconds
  const cardIndex = Math.floor(t / CARD_PERIOD);
  const isCat = cardIndex % 2 === 0;    // alternate cat/dog

  /* ── conveyor belt: card travels left→right to robot ── */
  const beltPath = [
    { x: 60,  y: 260 },
    { x: 210, y: 260 },
  ];
  // card arrives at robot hand at p≈0.9, then disappears
  const cardP = clamp(easeOut(cardPhase) * 1.1, 0, 1);
  const cardPos = pathLerp(beltPath, cardP);
  const cardVisible = cardPhase < 0.92;

  /* ── robot body (centre 400, 230) ── */
  const rx = 400, ry = 240;
  // hesitate early (first 3 cards), then get confident
  const confLevel = clamp(cardIndex * 0.18, 0, 1);          // 0→1 as more cards arrive
  const headWobble = wobble(t, 0.7, 6 * (1 - confLevel));   // wobble decreases with confidence
  const eyeY = ry - 28;
  const antennaLen = 20 + osc(t, 1.1, 0, 6);                // antenna bobs

  /* ── arm swings the card into the correct bin when arriving ── */
  // arm raises (0°→-50° for cat on right, or +50° for dog on left) then resets
  const armPhase = clamp((cardPhase - 0.7) / 0.22, 0, 1);
  const armSwing = isCat
    ? lerp(0, -55, easeOut(armPhase))
    : lerp(0, 55, easeOut(armPhase));

  /* ── bins: Cat (right, blue) and Dog (left, green) ── */
  const catBinX = 640, dogBinX = 160;
  const binY = 320;

  /* ── tossed card travels from robot to appropriate bin ── */
  const tossBezierCat = [
    { x: rx,      y: ry - 20 },
    { x: rx + 80, y: ry - 80 },
    { x: catBinX, y: binY - 20 },
  ];
  const tossBezierDog = [
    { x: rx,      y: ry - 20 },
    { x: rx - 80, y: ry - 80 },
    { x: dogBinX, y: binY - 20 },
  ];
  const tossPath = isCat ? tossBezierCat : tossBezierDog;
  const tossP = clamp((cardPhase - 0.88) / 0.12, 0, 1);
  const tossPos = pathLerp(tossPath, easeOut(tossP));
  const tossVisible = cardPhase > 0.88;

  /* ── "Smartness" star meter top-centre ── */
  const smartness = clamp(cardIndex * 0.12, 0, 1);    // fills up over ~8 cards
  const starCount = 5;
  const filledStars = Math.round(smartness * starCount);

  /* ── belt rollers scroll ── */
  const beltScroll = (t * 80) % 30;

  /* ── bin fill level (counted cards per type) ── */
  const totalCats = Math.floor((cardIndex + 1) / 2);
  const totalDogs = Math.floor(cardIndex / 2);

  return (
    <g opacity={opacity}>
      <defs>
        <linearGradient id="ml-bg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#f0f9ff" />
          <stop offset="100%" stopColor="#e0f2fe" />
        </linearGradient>
        <radialGradient id="ml-robot-body" cx="50%" cy="30%">
          <stop offset="0%" stopColor="#c7d2fe" />
          <stop offset="100%" stopColor="#818cf8" />
        </radialGradient>
      </defs>

      {/* Background */}
      <rect x={0} y={0} width={800} height={450} fill="url(#ml-bg)" />

      {/* ── Conveyor belt ── */}
      <rect x={50} y={268} width={180} height={18} rx={9} fill="#334155" />
      {/* scrolling belt stripes */}
      {Array.from({ length: 8 }, (_, i) => (
        <rect key={i} x={52 + ((i * 28 - beltScroll + 224) % 168)} y={270} width={14} height={14} rx={3} fill="#475569" />
      ))}
      {/* belt end rollers */}
      <circle cx={59}  cy={277} r={9} fill="#64748b" />
      <circle cx={221} cy={277} r={9} fill="#64748b" />

      {/* ── Card on belt ── */}
      {cardVisible && (
        <g transform={`translate(${cardPos.x} ${cardPos.y})`}>
          <rect x={-18} y={-22} width={36} height={36} rx={6} fill="#fff" stroke="#cbd5e1" strokeWidth={2} />
          <g transform="scale(0.65)">{isCat ? <CatFace /> : <DogFace />}</g>
        </g>
      )}

      {/* ── Dog bin (left) ── */}
      <g transform={`translate(${dogBinX} ${binY})`}>
        <rect x={-42} y={0} width={84} height={70} rx={8} fill="#dcfce7" stroke="#16a34a" strokeWidth={3} />
        <text x={0} y={-10} textAnchor="middle" fill="#15803d" fontSize={14} fontWeight={800} fontFamily="Inter,sans-serif">Dog</text>
        {/* count badge */}
        <rect x={26} y={-28} width={30} height={20} rx={10} fill="#16a34a" />
        <text x={41} y={-14} textAnchor="middle" fill="#fff" fontSize={12} fontWeight={800} fontFamily="Inter,sans-serif">{totalDogs}</text>
        {/* stack of cards inside bin */}
        {Array.from({ length: Math.min(totalDogs, 4) }, (_, i) => (
          <rect key={i} x={-30 + i * 4} y={4 + i * 3} width={36} height={28} rx={4} fill="#f0fdf4" stroke="#86efac" strokeWidth={1.5} />
        ))}
      </g>

      {/* ── Cat bin (right) ── */}
      <g transform={`translate(${catBinX} ${binY})`}>
        <rect x={-42} y={0} width={84} height={70} rx={8} fill="#ede9fe" stroke="#7c3aed" strokeWidth={3} />
        <text x={0} y={-10} textAnchor="middle" fill="#6d28d9" fontSize={14} fontWeight={800} fontFamily="Inter,sans-serif">Cat</text>
        {/* count badge */}
        <rect x={26} y={-28} width={30} height={20} rx={10} fill="#7c3aed" />
        <text x={41} y={-14} textAnchor="middle" fill="#fff" fontSize={12} fontWeight={800} fontFamily="Inter,sans-serif">{totalCats}</text>
        {Array.from({ length: Math.min(totalCats, 4) }, (_, i) => (
          <rect key={i} x={-30 + i * 4} y={4 + i * 3} width={36} height={28} rx={4} fill="#f5f3ff" stroke="#c4b5fd" strokeWidth={1.5} />
        ))}
      </g>

      {/* ── Tossed card in flight ── */}
      {tossVisible && (
        <g transform={`translate(${tossPos.x} ${tossPos.y})`}>
          <rect x={-16} y={-20} width={32} height={32} rx={5} fill="#fff" stroke={isCat ? '#7c3aed' : '#16a34a'} strokeWidth={2.5} />
          <g transform="scale(0.55)">{isCat ? <CatFace /> : <DogFace />}</g>
        </g>
      )}

      {/* ── Robot body ── */}
      <g transform={`translate(${rx + headWobble} ${ry})`}>
        {/* shadow */}
        <ellipse cx={0} cy={90} rx={46} ry={10} fill="rgba(0,0,0,0.13)" />
        {/* torso */}
        <rect x={-34} y={14} width={68} height={72} rx={12} fill="url(#ml-robot-body)" stroke="#4f46e5" strokeWidth={2.5} />
        {/* chest screen — shows thinking dots pulsing */}
        <rect x={-22} y={24} width={44} height={30} rx={6} fill="#1e1b4b" />
        {Array.from({ length: 3 }, (_, i) => {
          const px = -12 + i * 12;
          const py = osc(t, 0.5, 34, 42, stagger(i, 0.8));
          return <circle key={i} cx={px} cy={py} r={4} fill={['#f472b6', '#34d399', '#fbbf24'][i]} />;
        })}
        {/* belly button */}
        <circle cx={0} cy={68} r={7} fill="#4f46e5" />
        <circle cx={0} cy={68} r={4} fill="#818cf8" />
        {/* legs */}
        <rect x={-22} y={84} width={16} height={28} rx={6} fill="#6366f1" stroke="#4f46e5" strokeWidth={1.5} />
        <rect x={8}  y={84} width={16} height={28} rx={6} fill="#6366f1" stroke="#4f46e5" strokeWidth={1.5} />
        {/* feet */}
        <ellipse cx={-14} cy={114} rx={12} ry={7} fill="#4f46e5" />
        <ellipse cx={16}  cy={114} rx={12} ry={7} fill="#4f46e5" />

        {/* arms — the right arm swings to toss the card */}
        {/* left arm (idle gentle sway) */}
        <g transform={`rotate(${wobble(t, 2.4, 8)} -34 28)`}>
          <rect x={-52} y={18} width={20} height={42} rx={8} fill="#818cf8" stroke="#4f46e5" strokeWidth={1.5} />
          <ellipse cx={-42} cy={62} rx={10} ry={8} fill="#6366f1" />
        </g>
        {/* right arm (swings to sort) */}
        <g transform={`rotate(${armSwing} 34 28)`}>
          <rect x={32} y={18} width={20} height={42} rx={8} fill="#818cf8" stroke="#4f46e5" strokeWidth={1.5} />
          <ellipse cx={42} cy={62} rx={10} ry={8} fill="#6366f1" />
        </g>

        {/* head */}
        <rect x={-30} y={-56} width={60} height={54} rx={14} fill="#818cf8" stroke="#4f46e5" strokeWidth={2.5} />
        {/* antenna */}
        <line x1={0} y1={-56} x2={0} y2={-56 - antennaLen} stroke="#4f46e5" strokeWidth={3} strokeLinecap="round" />
        <circle cx={0} cy={-56 - antennaLen} r={5} fill={accentColor} />
        {/* eyes */}
        <rect x={-20} y={eyeY - ry - 6} width={14} height={14} rx={4} fill="#1e1b4b" />
        <rect x={8}   y={eyeY - ry - 6} width={14} height={14} rx={4} fill="#1e1b4b" />
        {/* eye shine — blinks as robot "looks" at card */}
        <circle cx={-13} cy={eyeY - ry + 1} r={3} fill="#38bdf8" opacity={osc(t, 0.9, 0.4, 1)} />
        <circle cx={15}  cy={eyeY - ry + 1} r={3} fill="#38bdf8" opacity={osc(t, 0.9, 0.4, 1)} />
        {/* smile gets bigger with confidence */}
        <path
          d={`M-${10 + confLevel * 8},${eyeY - ry + 14} Q0,${eyeY - ry + 22 + confLevel * 8} ${10 + confLevel * 8},${eyeY - ry + 14}`}
          fill="none" stroke="#1e1b4b" strokeWidth={2.5} strokeLinecap="round"
        />
      </g>

      {/* ── Smartness star meter ── */}
      <g transform="translate(400 68)">
        <rect x={-130} y={-26} width={260} height={46} rx={23} fill="rgba(255,255,255,0.9)" stroke="#c7d2fe" strokeWidth={2} />
        <text x={-116} y={5} fill="#4f46e5" fontSize={13} fontWeight={700} fontFamily="Inter,sans-serif">Smartness:</text>
        {Array.from({ length: starCount }, (_, i) => {
          const filled = i < filledStars;
          const starPulse = filled && i === filledStars - 1 ? osc(t, 0.6, 0.85, 1.25) : 1;
          return (
            <g key={i} transform={`translate(${-12 + i * 30} 0) scale(${starPulse})`}>
              <text x={0} y={7} textAnchor="middle" fontSize={20} fill={filled ? '#fbbf24' : '#e2e8f0'}>★</text>
            </g>
          );
        })}
      </g>

      {/* ── "Thinking hard" speech-bubble when early / hesitating ── */}
      {confLevel < 0.5 && cardPhase > 0.3 && cardPhase < 0.75 && (
        <g transform={`translate(${rx + 60} ${ry - 80})`}>
          <rect x={0} y={-22} width={72} height={28} rx={14} fill="#fff" stroke="#c7d2fe" strokeWidth={1.5} />
          <polygon points="-10,8 6,8 0,20" fill="#fff" stroke="#c7d2fe" strokeWidth={1.5} />
          <text x={36} y={-3} textAnchor="middle" fill="#6366f1" fontSize={11} fontWeight={700} fontFamily="Inter,sans-serif">Hmm…</text>
        </g>
      )}

      {/* ── Caption ── */}
      <g transform="translate(400 422)">
        <rect x={-270} y={-22} width={540} height={38} rx={19} fill="rgba(255,255,255,0.92)" stroke="#c7d2fe" strokeWidth={2} />
        <text x={0} y={4} textAnchor="middle" fill="#1e1b4b" fontSize={15} fontWeight={700} fontFamily="Inter,sans-serif">{label}</text>
      </g>
    </g>
  );
};

/* ━━━ Neural Network: signal pulse travels L→R through glowing nodes ━━━━━━━ */

export interface NeuralNetworkSceneProps {
  opacity?: number;
  accentColor?: string;
  label?: string;
  t?: number;
}

export const NeuralNetworkScene: React.FC<NeuralNetworkSceneProps> = ({
  opacity = 1,
  accentColor = '#ec4899',
  label = 'AI passes the idea through tiny thinking dots!',
  t = 0,
}) => {
  /* ── pulse travels 0→1 every 5s, looping ── */
  const PULSE_PERIOD = 5.0;
  const pulseP = loop(t, PULSE_PERIOD);          // 0→1 each cycle

  /* ── network layout ── */
  // Columns (layers): input=1 node, hidden1=3 nodes, hidden2=3 nodes, output=1 node
  const layers: Array<{ x: number; nodes: number }> = [
    { x: 160, nodes: 1 },
    { x: 320, nodes: 3 },
    { x: 490, nodes: 3 },
    { x: 640, nodes: 1 },
  ];
  const networkCY = 220;
  const nodeSpacing = 70;

  // Compute node positions
  const nodePositions = layers.map(layer =>
    Array.from({ length: layer.nodes }, (_, ni) => ({
      x: layer.x,
      y: networkCY + (ni - (layer.nodes - 1) / 2) * nodeSpacing,
    })),
  );

  /* ── chosen signal path (fixed, deterministic): always the middle nodes ── */
  // path: input[0] → hidden1[1] → hidden2[1] → output[0]
  const signalPath = [
    nodePositions[0][0],
    nodePositions[1][1],
    nodePositions[2][1],
    nodePositions[3][0],
  ];

  /* Signal position along its path */
  // The signal travels from layer 0 to layer 3; scale pulseP over [0.05, 0.85]
  const travelProgress = clamp((pulseP - 0.05) / 0.8, 0, 1);
  const sigPos = pathLerp(signalPath, easeOut(travelProgress));

  /* Which layer the signal has reached (to glow nodes it's passed) */
  const layerProgress = travelProgress * (signalPath.length - 1);  // 0→3

  /* ── colours ── */
  const litColor   = '#fbbf24';
  const dimColor   = '#312e81';
  const edgeNormal = '#4f46e5';

  /* Input card flip: shows cat picture, then when output is reached => "Cat!" */
  const outputArrived = pulseP > 0.88;

  /* Output card ripple when signal arrives */
  const outputPop = outputArrived ? osc(t, 0.55, 0.9, 1.12) : 1;

  /* Brain-head motif sitting above the network */
  const brainBob = wobble(t, 2.6, 4);
  const brainGlow = osc(t, 1.8, 0.5, 1);

  return (
    <g opacity={opacity}>
      <defs>
        <radialGradient id="nn-bg" cx="50%" cy="50%">
          <stop offset="0%" stopColor="#1e1b4b" />
          <stop offset="100%" stopColor="#0f172a" />
        </radialGradient>
        <radialGradient id="nn-node-lit" cx="50%" cy="50%">
          <stop offset="0%" stopColor="#fef3c7" />
          <stop offset="60%" stopColor={litColor} />
          <stop offset="100%" stopColor="#f59e0b" />
        </radialGradient>
        <radialGradient id="nn-node-dim" cx="50%" cy="50%">
          <stop offset="0%" stopColor="#4c1d95" />
          <stop offset="100%" stopColor={dimColor} />
        </radialGradient>
        <filter id="nn-glow">
          <feGaussianBlur stdDeviation="4" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>

      {/* Background */}
      <rect x={0} y={0} width={800} height={450} fill="url(#nn-bg)" />

      {/* Subtle grid */}
      {Array.from({ length: 16 }, (_, i) => (
        <line key={`gv${i}`} x1={i * 54} y1={0} x2={i * 54} y2={450} stroke="#1e2657" strokeWidth={0.8} opacity={0.6} />
      ))}
      {Array.from({ length: 9 }, (_, i) => (
        <line key={`gh${i}`} x1={0} y1={i * 54} x2={800} y2={i * 54} stroke="#1e2657" strokeWidth={0.8} opacity={0.6} />
      ))}

      {/* ── Brain / robot-head motif top-centre ── */}
      <g transform={`translate(400 ${70 + brainBob})`} opacity={brainGlow}>
        {/* halo glow ring */}
        <circle r={38} fill="none" stroke={accentColor} strokeWidth={4} opacity={0.25} />
        <circle r={30} fill="#312e81" stroke="#6d28d9" strokeWidth={2.5} />
        {/* two brain-lobe bumps */}
        <ellipse cx={-12} cy={-8} rx={16} ry={14} fill="#4c1d95" stroke="#7c3aed" strokeWidth={1.5} />
        <ellipse cx={12}  cy={-8} rx={16} ry={14} fill="#4c1d95" stroke="#7c3aed" strokeWidth={1.5} />
        {/* centre fold */}
        <line x1={0} y1={-20} x2={0} y2={4} stroke="#6d28d9" strokeWidth={2} strokeLinecap="round" />
        {/* eyes */}
        <circle cx={-10} cy={14} r={5} fill="#1e1b4b" />
        <circle cx={10}  cy={14} r={5} fill="#1e1b4b" />
        <circle cx={-8.5} cy={12.5} r={2} fill="#38bdf8" opacity={0.9} />
        <circle cx={11.5} cy={12.5} r={2} fill="#38bdf8" opacity={0.9} />
        {/* pulse dot on top */}
        <circle cx={0} cy={-28} r={5} fill={accentColor} opacity={osc(t, 0.7, 0.4, 1)} />
      </g>

      {/* ── All edges (connections) ── */}
      {layers.slice(0, -1).flatMap((_layer, li) =>
        nodePositions[li].flatMap((from, fi) =>
          nodePositions[li + 1].map((to, ti) => {
            // Is this edge on the signal path?
            const onPath =
              signalPath[li].x === from.x && signalPath[li].y === from.y &&
              signalPath[li + 1].x === to.x && signalPath[li + 1].y === to.y;
            const passed = onPath && layerProgress > li + 0.3;
            return (
              <line
                key={`e${li}-${fi}-${ti}`}
                x1={from.x} y1={from.y}
                x2={to.x}   y2={to.y}
                stroke={passed ? litColor : edgeNormal}
                strokeWidth={passed ? 3.5 : 1.5}
                opacity={passed ? 0.95 : 0.35}
                strokeLinecap="round"
              />
            );
          }),
        ),
      )}

      {/* ── Nodes ── */}
      {nodePositions.flatMap((layerNodes, li) =>
        layerNodes.map((node, ni) => {
          const isOnPath = signalPath.some(p => p.x === node.x && p.y === node.y);
          const nodeDepth = signalPath.findIndex(p => p.x === node.x && p.y === node.y);
          const isLit = isOnPath && layerProgress > nodeDepth - 0.2;
          const glowR = isLit ? osc(t, 0.6, 20, 26, stagger(ni, 0.4)) : 18;
          return (
            <g key={`n${li}-${ni}`} filter={isLit ? 'url(#nn-glow)' : undefined}>
              <circle
                cx={node.x} cy={node.y} r={glowR}
                fill={isLit ? 'url(#nn-node-lit)' : 'url(#nn-node-dim)'}
                stroke={isLit ? '#fde68a' : '#6d28d9'}
                strokeWidth={isLit ? 3 : 1.5}
              />
              {/* inner dot */}
              <circle cx={node.x} cy={node.y} r={6} fill={isLit ? '#fff7cc' : '#1e1b4b'} />
            </g>
          );
        }),
      )}

      {/* ── Signal pulse ball travelling the path ── */}
      {travelProgress > 0 && travelProgress < 1 && (
        <g filter="url(#nn-glow)">
          <circle cx={sigPos.x} cy={sigPos.y} r={14} fill={accentColor} opacity={0.9} />
          <circle cx={sigPos.x} cy={sigPos.y} r={8}  fill="#fff" />
        </g>
      )}

      {/* ── Layer labels ── */}
      {[{ x: 160, lbl: 'Input' }, { x: 320, lbl: 'Think 1' }, { x: 490, lbl: 'Think 2' }, { x: 640, lbl: 'Output' }].map(({ x, lbl }) => (
        <text key={lbl} x={x} y={330} textAnchor="middle" fill="#94a3b8" fontSize={11} fontWeight={600} fontFamily="Inter,sans-serif">{lbl}</text>
      ))}

      {/* ── Input card: cat picture ── */}
      <g transform="translate(98 220)">
        <rect x={-30} y={-36} width={60} height={60} rx={10} fill="#1e293b" stroke="#4f46e5" strokeWidth={2.5} />
        <g transform="scale(0.75)"><CatFace /></g>
        <text x={0} y={34} textAnchor="middle" fill="#94a3b8" fontSize={10} fontWeight={600} fontFamily="Inter,sans-serif">? ? ?</text>
      </g>

      {/* ── Output card: flips to "Cat!" when signal arrives ── */}
      <g transform={`translate(702 220) scale(${outputPop})`}>
        <rect x={-36} y={-36} width={72} height={60} rx={10}
          fill={outputArrived ? '#14532d' : '#1e293b'}
          stroke={outputArrived ? '#22c55e' : '#4f46e5'}
          strokeWidth={2.5}
        />
        {outputArrived
          ? (
            <g>
              <g transform="scale(0.68)"><CatFace /></g>
              <text x={0} y={32} textAnchor="middle" fill="#4ade80" fontSize={15} fontWeight={800} fontFamily="Inter,sans-serif">Cat!</text>
            </g>
          )
          : (
            <text x={0} y={8} textAnchor="middle" fill="#475569" fontSize={22} fontWeight={800} fontFamily="Inter,sans-serif">?</text>
          )
        }
      </g>

      {/* ── Right-arrow between layers label ── */}
      {[{ x: 240, y: 388 }, { x: 406, y: 388 }, { x: 568, y: 388 }].map(({ x, y }, i) => (
        <g key={i} transform={`translate(${x} ${y})`}>
          <line x1={-14} y1={0} x2={14} y2={0} stroke="#334155" strokeWidth={2} />
          <polygon points="14,0 7,-5 7,5" fill="#334155" />
        </g>
      ))}

      {/* ── Caption ── */}
      <g transform="translate(400 422)">
        <rect x={-280} y={-22} width={560} height={38} rx={19} fill="rgba(15,23,42,0.92)" stroke="#4f46e5" strokeWidth={2} />
        <text x={0} y={4} textAnchor="middle" fill="#e2e8f0" fontSize={15} fontWeight={700} fontFamily="Inter,sans-serif">{label}</text>
      </g>
    </g>
  );
};
